import { api } from "@/lib/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CompanyCodeParams,
  CompanyCodeResponse,
  StCompanyCode,
  InsertCompanyCodeRequest,
} from "@/interface/standard/st_company_code";
import { toast } from "sonner";

/**
 * 모든 업체 코드 조회
 * @param params 페이지 번호, 페이지 크기
 * @returns 업체 코드 데이터
 */
export const useCompanyCode = (params: CompanyCodeParams = {}) => {
  const { page = 0, size = 20, search, exactMatch = false, sort } = params;

  return useQuery<CompanyCodeResponse>({
    queryKey: ["companyCode", page, size, search, exactMatch, sort],
    queryFn: async () => {
      const response = await api.get("/company-code", {
        params: {
          page,
          size,
          stCompanyName: search,
          exactMatch,
          sort,
        },
      });
      return response.data;
    },
    staleTime: 1000 * 60, // 1분간 캐시 유지
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const useInsertCompanyCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertCompanyCodeRequest) => {
      const response = await api.post("/company-code", data);
      return response.data;
    },
    onSuccess: res => {
      console.log(res);
      toast.success("업체 코드 추가 성공");
      // 모든 companyCode 쿼리 무효화 및 캐시 초기화
      queryClient.invalidateQueries({ queryKey: ["companyCode"] });
      queryClient.refetchQueries({ queryKey: ["companyCode"] });
    },
    onError: error => {
      console.error("Error inserting company code:", error);
      toast.error("업체 코드 추가 실패" + error);
    },
  });
};

export const useUpdateCompanyCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: InsertCompanyCodeRequest }) => {
      const response = await api.put(`/company-code/${id}`, data);
      return response.data;
    },
    onSuccess: res => {
      console.log(res);
      toast.success("업체 코드 수정 성공");
      // 모든 companyCode 쿼리 무효화 및 캐시 초기화
      queryClient.invalidateQueries({ queryKey: ["companyCode"] });
      queryClient.refetchQueries({ queryKey: ["companyCode"] });
    },
    onError: error => {
      console.error("Error updating company code:", error);
      toast.error("업체 코드 수정 실패" + error);
    },
  });
};

export const useDeleteCompanyCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteCompanyCode"],
    mutationFn: async (id: number[]) => {
      const response = await api.delete(`/company-code`, { data: id });
      return response.data;
    },
    onSuccess: res => {
      console.log(res);
      toast.success("업체 코드 삭제 성공");
      // 모든 companyCode 쿼리 무효화 및 캐시 초기화
      queryClient.invalidateQueries({ queryKey: ["companyCode"] });
      queryClient.refetchQueries({ queryKey: ["companyCode"] });
    },
    onError: error => {
      console.error("Error deleting company code:", error);
      toast.error("업체 코드 삭제 실패" + error);
    },
  });
};

/**
 * 화주사 비밀번호 초기화 (1234로 설정)
 */
export const useResetShipperPassword = () => {
  return useMutation({
    mutationKey: ["resetShipperPassword"],
    mutationFn: async (stCompanyCode: string) => {
      const response = await api.post("/shipper/set-password", {
        id: stCompanyCode,
        password: "1234",
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success("비밀번호가 1234로 초기화되었습니다.");
    },
    onError: error => {
      console.error("Error resetting password:", error);
      toast.error("비밀번호 초기화 실패");
    },
  });
};
